/**
 * DeleteAccountCard — Profile settings "Danger zone" section.
 *
 * Renders a red-outlined card with a "Delete My Account" button that opens
 * a two-step confirmation dialog before POSTing to
 * /api/auth/account/request-deletion.
 *
 * Required for App Store & Google Play compliance — every account holder
 * must be able to trigger permanent deletion from within the app itself.
 *
 * On success: soft-deactivates the account, wipes sessions, and forces the
 * user out (redirect to /login). Admins are notified in-app + by email so
 * they can process the physical purge within 30 days.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Trash2, AlertTriangle, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccountCard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('warning'); // warning | confirm | processing
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep('warning');
    setReason('');
    setConfirmText('');
    setLoading(false);
  };

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      toast.error('Please type DELETE to confirm.');
      return;
    }
    setLoading(true);
    setStep('processing');
    try {
      const res = await fetch(`${API}/api/auth/account/request-deletion`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true, reason: reason || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to submit request. Please try again or email support@nobleempirecs.com.');
        setLoading(false);
        setStep('confirm');
        return;
      }
      toast.success(
        'Deletion processing. Account access removed; required business records are retained.',
        { duration: 6000 },
      );
      // Log the user out — session is already invalidated server-side.
      try { localStorage.removeItem('session_token'); localStorage.removeItem('token'); } catch (_) { /* ignore */ }
      setTimeout(() => {
        setOpen(false);
        navigate('/login', { replace: true });
        window.location.reload();
      }, 1500);
    } catch (_err) {
      toast.error('Network error — please try again.');
      setLoading(false);
      setStep('confirm');
    }
  };

  return (
    <>
      <Card className="p-4 bg-red-50 border-2 border-red-300" data-testid="delete-account-card">
        <div className="flex items-start gap-3">
          <ShieldOff className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-red-900 flex items-center gap-2">
              Danger zone &mdash; Delete my account
            </div>
            <p className="text-xs text-red-800 mt-1">
              Permanently delete your NECS account and personal app data. This
              cannot be undone. Access ends immediately; personal data deletion
              is processed within 30 days. Required employment, payroll, safety,
              service evidence and financial records are retained. Alternatively, email{' '}
              <a
                href="mailto:support@nobleempirecs.com"
                className="underline font-semibold"
                data-testid="delete-account-email-link"
              >
                support@nobleempirecs.com
              </a>.
            </p>
            <Button
              onClick={() => { reset(); setOpen(true); }}
              className="mt-3 bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="delete-account-open-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete my account
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto bg-zinc-950 border-2 border-red-600 text-white"
          data-testid="delete-account-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {step === 'warning'   && 'Delete your NECS account?'}
              {step === 'confirm'   && 'Are you absolutely sure?'}
              {step === 'processing' && 'Submitting your request…'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {step === 'warning' && (
            <div className="space-y-3 text-sm text-zinc-300">
              <p>Deleting your account will:</p>
              <ul className="list-disc pl-6 space-y-1 text-zinc-400">
                <li>Immediately sign you out and prevent any future login.</li>
                <li>Remove your name, phone, email, notification preferences, and profile data.</li>
                <li>Remove personal app credentials, sessions, preferences and profile data.</li>
                <li>Retain required employment, timekeeping, service, safety and financial records under the privacy policy.</li>
              </ul>
              <p className="text-xs text-zinc-500">
                Certain records required by law (billing invoices, safety incident reports) will be retained
                for the minimum legal period per our Privacy Policy.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                  data-testid="delete-account-cancel-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => setStep('confirm')}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold"
                  data-testid="delete-account-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="del-reason" className="text-xs text-zinc-400">Reason (optional)</Label>
                <Input
                  id="del-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Help us improve — why are you leaving?"
                  className="bg-zinc-900 border-zinc-700 text-white"
                  data-testid="delete-account-reason"
                  maxLength={500}
                />
              </div>
              <div>
                <Label htmlFor="del-confirm" className="text-xs text-red-400 font-semibold">
                  Type DELETE to confirm
                </Label>
                <Input
                  id="del-confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="bg-zinc-900 border-red-500 text-white font-mono uppercase"
                  data-testid="delete-account-confirm-text"
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('warning')}
                  className="border-zinc-700 text-zinc-300"
                  data-testid="delete-account-back"
                >
                  Back
                </Button>
                <Button
                  onClick={submit}
                  disabled={loading || confirmText.trim().toUpperCase() !== 'DELETE'}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold"
                  data-testid="delete-account-final-submit"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete my account permanently
                </Button>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-sm text-zinc-400 py-4 text-center" data-testid="delete-account-processing">
              Submitting your deletion request…
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
